import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
  Input,
  Type,
  ViewEncapsulation,
  ComponentRef,
  ChangeDetectorRef,
  Injector,
  ApplicationRef,
  createComponent,
  EnvironmentInjector,
  NgZone,
  AfterViewInit,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  Renderer2,
  ElementRef,
} from '@angular/core';

import { ComponentRegistryService } from '../../services/component-registry/component-registry.service';
import { TemplateService } from '../../services/template/template.service';
import { CustomComponent } from '../../models/CustomComponent.model';
import { Subject, merge } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ComponentContainerComponent } from '../component-container/component-container.component';
import { environment } from '../../environment/environment';
import { AuthService } from '../../services/auth/auth.service';
import { PermissionEvaluatorService } from '../../services/component-permission/permission-evaluator.service';
import { ActivatedRoute } from '@angular/router';
import { ComponentInputBase } from '../../models/ComponentInputBase.model';

@Component({
  selector: 'app-dynamic-component',
  standalone: true,
  imports: [],
  templateUrl: './dynamic-component.component.html',
  styleUrl: './dynamic-component.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicComponentComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  private componentCreated = false;
  private lastComponentUuid: string | undefined;
  private lastInputsHash: string | undefined;
  private destroy$ = new Subject<void>();

  constructor(
    public templateService: TemplateService,
    public componentRegistry: ComponentRegistryService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private permissionEvaluator: PermissionEvaluatorService,
    private route: ActivatedRoute
  ) {}

  @Input() customComponent: CustomComponent | undefined;

  @Input() editorMode: boolean = false;

  @ViewChild('dynamicContainer', { read: ViewContainerRef, static: true })
  container!: ViewContainerRef;

  @Output() afterViewInit = new EventEmitter<void>();

  private componentRef: ComponentRef<any> | null = null;

  ngOnInit(): void {
    merge(
      this.permissionEvaluator.onPermissionVisibilityChanged(),
      this.authService.authStateReady$.pipe(filter(Boolean))
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        void this.retryCreateAfterPermissionReady();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['customComponent']?.currentValue) {
      const current = changes['customComponent'].currentValue;

      // Ensure we have a proper CustomComponent instance
      this.ensureCustomComponentInstance(current);

      // If conversion was needed, wait a bit for it to complete
      if (
        !this.customComponent ||
        typeof this.customComponent.getComponent !== 'function'
      ) {
        setTimeout(() => {
          this.processComponentChanges(current);
        }, 50);
        return;
      }

      this.processComponentChanges(current);
    }
  }

  private processComponentChanges(current: any): void {
    const currentInputsHash = this.getInputsHash(this.customComponent?.inputs);

    if (this.customComponent) {
      this.customComponent.editorMode = this.editorMode;
    }

    // Check if this is the same component instance and inputs haven't changed
    const isSameComponent =
      this.lastComponentUuid === this.customComponent?.uuid;
    const inputsChanged = this.lastInputsHash !== currentInputsHash;

    if (!isSameComponent || !this.componentCreated) {
      this.componentCreated = false;
      this.lastComponentUuid = this.customComponent?.uuid;
      this.lastInputsHash = currentInputsHash;
      if (this.container) {
        void this.createComponents();
      }
    } else if (inputsChanged && this.componentRef) {
      this.lastInputsHash = currentInputsHash;
      this.updateComponentInputs();
    } else if (
      isSameComponent &&
      this.componentRef &&
      this.customComponent?.customCSS
    ) {
      // Apply customCSS even if inputs haven't changed
      this.applyCustomCSS(this.componentRef, this.customComponent.customCSS);
    }
  }

  /**
   * Ensures that the customComponent is a proper CustomComponent instance
   * If it's a DTO or plain object, converts it to a CustomComponent instance
   */
  private ensureCustomComponentInstance(component: any): void {
    // Check if it's already a CustomComponent instance (has getComponent method)
    if (component && typeof component.getComponent === 'function') {
      this.customComponent = component;
      return;
    }

    // If it's a DTO or plain object, convert it to a CustomComponent instance
    if (component && component.name) {

      try {
        // Convert DTO to CustomComponent instance using the imported class
        this.customComponent = CustomComponent.fromDTO(component);
      } catch (error) {
        console.error(
          '[DynamicComponent] Error converting to CustomComponent:',
          error
        );
        // Fallback: create a basic CustomComponent instance
        this.customComponent = new CustomComponent(
          component.name || 'UnknownComponent',
          component.x || 0,
          component.y || 0,
          component.cols || 1,
          component.rows || 1,
          component.customCSS || '',
          component.settings || {},
          component.inputs || {},
          component.mode || 1,
          component.uuid
        );
      }
    } else {
      console.warn('[DynamicComponent] Invalid component data:', component);
      this.customComponent = undefined;
    }
  }

  private getInputsHash(inputs: any): string {
    if (!inputs) return '';
    const relevantData = Object.entries(inputs).map(([key, value]) => ({
      key,
      value: (value as any).value,
      type: (value as any).type,
    }));
    return JSON.stringify(relevantData);
  }

  private updateComponentInputs() {
    if (!this.componentRef || !this.customComponent?.inputs) return;

    if (this.componentRef.instance) {
      // Create a new inputs object to force change detection
      const newInputs = { ...this.customComponent.inputs };
      const instance = this.componentRef.instance;

      // ComponentInputBase subclasses expose dynamicInput setter for batch updates
      if (instance instanceof ComponentInputBase) {
        instance.dynamicInput = newInputs;
      } else {
        Object.entries(newInputs).forEach(([key, value]) => {
          instance[key] = value.value;

          if (instance.inputs) {
            instance.inputs[key] = { ...value };
          }
        });
      }

      // Update the customComponent reference with a new object
      instance.customComponent = { ...this.customComponent };

      // Update the instance reference
      this.customComponent.componentInstance = this.componentRef.instance;

      // Apply custom CSS if available
      if (this.customComponent.customCSS) {
        this.applyCustomCSS(this.componentRef, this.customComponent.customCSS);
      }

      // Force change detection only once
      this.cdr.markForCheck();
    }
  }

  ngAfterViewInit() {
    this.afterViewInit.emit();
  }

  private async ensureComponentLoaded(): Promise<void> {
    if (!this.customComponent || !this.container) {
      return;
    }

    // Check if getComponent method exists
    if (typeof this.customComponent.getComponent !== 'function') {
      console.error(
        `getComponent method not found on customComponent: ${this.customComponent.name}`
      );
      return;
    }

    // Keep trying until component is available or max attempts reached
    let attempts = 0;
    const maxAttempts = 5;

    while (
      !this.isComponent(this.customComponent.component) &&
      attempts < maxAttempts
    ) {
      await new Promise((resolve) => setTimeout(resolve, 200)); // Increased delay
      this.customComponent.component = this.customComponent.getComponent(
        this.componentRegistry
      );
      attempts++;
    }

    if (!this.isComponent(this.customComponent.component)) {
      console.error(
        `Failed to load component ${this.customComponent.name} after ${maxAttempts} attempts`
      );
    }
  }

  async createComponents() {
    if (!this.customComponent || !this.container || this.componentCreated) {
      return;
    }

    if (!this.isComponentVisible()) {
      this.componentCreated = true;
      this.renderer.setStyle(this.elementRef.nativeElement, 'display', 'none');
      return;
    }

    if (this.permissionEvaluator.needsPermissionCheck(this.customComponent)) {
      this.permissionEvaluator.ensurePermissionChecked(
        this.customComponent,
        null,
        this.route
      );
      if (
        !this.permissionEvaluator.getCachedPermissionVisible(
          this.customComponent
        )
      ) {
        this.componentCreated = true;
        this.renderer.setStyle(
          this.elementRef.nativeElement,
          'display',
          'none'
        );
        return;
      }
    }

    // Ensure we have a proper CustomComponent instance before proceeding
    if (typeof this.customComponent.getComponent !== 'function') {
      // Wait a bit for the conversion to complete
      await new Promise((resolve) => setTimeout(resolve, 100));

      if (typeof this.customComponent.getComponent !== 'function') {
        console.error(
          `getComponent method not found on customComponent: ${this.customComponent.name}`
        );
        return;
      }
    }

    try {
      await this.ensureComponentLoaded();

      if (this.isComponent(this.customComponent.component)) {
        // Only clear and recreate if necessary
        if (
          !this.componentRef ||
          this.lastComponentUuid !== this.customComponent.uuid
        ) {
          this.container.clear();
          if (this.componentRef) {
            this.componentRef.destroy();
            this.componentRef = null;
          }

          this.componentRef = this.container.createComponent(
            this.customComponent.component
          );

          if (this.componentRef.instance) {
            this.componentRef.instance.customComponent = this.customComponent;
          }

          this.customComponent.componentInstance = this.componentRef.instance;
          this.lastComponentUuid = this.customComponent.uuid;
          this.componentCreated = true;

          // Apply the duna-full-bleed class when the CMS setting is enabled.
          // This tells the global 14px side-padding rule (styles.scss) to skip this component
          // so it can span edge-to-edge (e.g. dynamic-tabs, full-bleed hero sections).
          // NOTE: we only ever ADD the class here, never remove it. Components that need
          // full-bleed by default declare  host: { class: 'duna-full-bleed' }  in their own
          // @Component decorator — removing it here would fight Angular's host binding.
          // The CMS checkbox is therefore additive: it opts any component into full-bleed.
          if (this.customComponent.settings?.['fullBleed']?.value) {
            this.renderer.addClass(
              this.componentRef.location.nativeElement,
              'duna-full-bleed'
            );
          }

          // Apply custom CSS if available
          if (this.customComponent.customCSS) {
            this.applyCustomCSS(
              this.componentRef,
              this.customComponent.customCSS
            );
          }

          // Update inputs
          this.updateComponentInputs();

          // Force change detection only once
          this.cdr.markForCheck();
        }
      } else {
        console.warn(
          'Failed to get valid component:',
          this.customComponent.name
        );
      }
    } catch (error) {
      console.error('Error creating component:', error);
    }
  }

  private isComponent(component: any): component is Type<any> {
    return (
      typeof component === 'function' ||
      component instanceof Function ||
      (component && component.ɵcmp !== undefined)
    );
  }

  private applyCustomCSS(
    componentRef: ComponentRef<any>,
    customCSS: string | object
  ) {
    if (!componentRef?.location?.nativeElement) return;

    // Get both the component's native element and the host element (app-dynamic-component)
    const element = componentRef.location.nativeElement;
    const hostElement = this.elementRef.nativeElement;

    // Handle string format
    if (typeof customCSS === 'string') {
      // Apply directly to style.cssText for the component element
      element.style.cssText = customCSS;

      // Apply to the host element using Renderer2
      this.applyCustomCSSToHost(customCSS);

    }
    // Handle object format
    else if (typeof customCSS === 'object' && customCSS !== null) {
      // Apply each style individually to the component element
      Object.entries(customCSS).forEach(([property, value]) => {
        if (typeof value === 'string') {
          element.style[property] = value;
        }
      });

      // Convert object to string for the host element
      const cssString = Object.entries(customCSS)
        .filter(([_, value]) => typeof value === 'string')
        .map(([property, value]) => `${property}: ${value};`)
        .join(' ');

      this.applyCustomCSSToHost(cssString);

    }
  }

  private applyCustomCSSToHost(cssString: string) {
    if (!cssString) return;

    const hostElement = this.elementRef.nativeElement;

    // Parse the CSS string into individual properties
    const rules = cssString.split(';').filter((rule) => rule.trim());

    rules.forEach((rule) => {
      const [property, value] = rule.split(':').map((part) => part.trim());
      if (property && value) {
        // Use Renderer2 to set styles (safer than direct manipulation)
        this.renderer.setStyle(hostElement, property, value);
      }
    });

  }

  isComponentVisible(): boolean {
    const allowedRoles = this.customComponent?.settings['allowedRoles']?.value;

    // Get user roles and auth state
    const userRoles = this.authService.getUserRoles();

    // Debug logging

    // SuperAdmin can always see everything
    if (userRoles.includes('superadmin') || userRoles.includes('Superadmin')) {
      return true;
    }

    // If no allowed roles specified, component is visible to all
    if (!allowedRoles || allowedRoles.length === 0) {
      return true;
    }

    // Check if user has ANY of the roles that ARE allowed to see the component
    // If they do, return true (component is visible to them)
    const hasAllowedRole = allowedRoles.some((role: string) =>
      userRoles.includes(role)
    );
    return hasAllowedRole;
  }

  private async retryCreateAfterPermissionReady(): Promise<void> {
    if (!this.customComponent || !this.container || this.componentRef) {
      return;
    }

    if (!this.isComponentVisible()) {
      return;
    }

    if (this.permissionEvaluator.needsPermissionCheck(this.customComponent)) {
      this.permissionEvaluator.ensurePermissionChecked(
        this.customComponent,
        null,
        this.route
      );
      if (
        !this.permissionEvaluator.getCachedPermissionVisible(
          this.customComponent
        )
      ) {
        return;
      }
    }

    if (!this.componentCreated) {
      return;
    }

    this.componentCreated = false;
    this.renderer.removeStyle(this.elementRef.nativeElement, 'display');
    await this.createComponents();
  }
}
